import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FiActivity,
  FiArrowRight,
  FiCheckSquare,
  FiFileText,
  FiMapPin,
  FiRefreshCw,
} from "react-icons/fi";
import TravelExpenseTable from "../components/travelExpense/TravelExpenseTable";
import PaginationControls from "../components/PaginationControls";
import { apiURL } from "../config/config";
import {
  canApproveTravelExpense,
  canCreateTravelExpense,
  isAdmin,
} from "../utils/buttonVisibility";
import {
  getTravelQuotationDisplayLabel,
  getTravelQuotationProjectLabel,
} from "../components/travelExpense/TravelExpenseApprovalUtils";

const normalizeRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

const TravelExpense = () => {
  const pageSize = 10;
  const navigate = useNavigate();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchRecords = useCallback(async () => {
    try {
      setLoadingRecords(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiURL}travel-expenses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const allRecords = normalizeRecords(response.data);
      const ownRecords = allRecords
        .filter((item) => String(item?.requestedBy || "").trim() === user.username)
        .map((item) => ({
          ...item,
          quotationDisplayNumber: getTravelQuotationDisplayLabel(item),
          quotationProjectLabel: getTravelQuotationProjectLabel(item),
        }))
        .sort(
          (a, b) =>
            new Date(b?.departureDateTime || b?.createdAt || 0) -
            new Date(a?.departureDateTime || a?.createdAt || 0)
        );

      setRecords(ownRecords);
    } catch (error) {
      console.error("Fetch travel expenses error:", error);
      toast.error(
        error?.response?.data?.message ||
          "ไม่สามารถโหลดรายการเบิกค่าเดินทางได้"
      );
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }, [user.username]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totalPages = Math.max(1, Math.ceil(records.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return records.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, records]);

  const handleEdit = (item) => {
    navigate(`/travel-expense/request/${item._id}`);
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "ลบรายการนี้?",
      text: "การลบจะไม่สามารถกู้คืนได้",
      showCancelButton: true,
      confirmButtonText: "ลบรายการ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(item._id);
      const token = localStorage.getItem("token");
      await axios.delete(`${apiURL}travel-expenses/${item._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setRecords((prev) => prev.filter((record) => record._id !== item._id));
      toast.success("ลบรายการเบิกค่าเดินทางเรียบร้อย");
    } catch (error) {
      console.error("Delete travel expense error:", error);
      toast.error(
        error?.response?.data?.message || "ไม่สามารถลบรายการเบิกค่าเดินทางได้"
      );
    } finally {
      setDeletingId("");
    }
  };

  const cards = [
    ...(canCreateTravelExpense()
      ? [
          {
            title: "Create Request",
            description: "สร้างคำขอเบิกค่าเดินทาง พร้อมคำนวณระยะทางและแนบเอกสาร",
            to: "/travel-expense/request",
            icon: <FiFileText size={20} />,
            accent:
              "border-cyan-200 dark:border-cyan-800 from-cyan-500/10 to-sky-500/10",
          },
        ]
      : []),
    ...(canApproveTravelExpense()
      ? [
          {
            title: "Approve Requests",
            description:
              "ตรวจสอบ อนุมัติ หรือปฏิเสธรายการ Travel Expense ที่รอดำเนินการ",
            to: "/travel-expense/approve",
            icon: <FiCheckSquare size={20} />,
            accent:
              "border-emerald-200 dark:border-emerald-800 from-emerald-500/10 to-lime-500/10",
          },
        ]
      : []),
    ...(isAdmin()
      ? [
          {
            title: "Logs",
            description:
              "ดูประวัติการแก้ไข การอนุมัติ การปฏิเสธ และการลบรายการเบิกค่าเดินทางย้อนหลัง",
            to: "/travel-expense/logs",
            icon: <FiActivity size={20} />,
            accent:
              "border-sky-200 dark:border-sky-800 from-sky-500/10 to-cyan-500/10",
          },
        ]
      : []),
  ];

  return (
    <div className="px-4 sm:px-6 py-5 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="mb-8 rounded-3xl border border-cyan-100 dark:border-cyan-900/40 bg-gradient-to-br from-white via-cyan-50/60 to-sky-50/70 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-700 dark:text-cyan-300 shrink-0">
            <FiMapPin size={22} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
              Travel Expense Dashboard
            </h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl">
              จัดการงานเบิกค่าเดินทางจากศูนย์กลางเดียว เลือกสร้างคำขอใหม่ ตรวจสอบรายการอนุมัติ และติดตามคำขอของคุณได้ในหน้าเดียว
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {cards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className={`group rounded-2xl border bg-gradient-to-br ${card.accent} bg-white dark:bg-gray-800 p-5 sm:p-6 shadow-sm hover:shadow-md transition-all`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/80 dark:bg-gray-900/50 border border-white/80 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                  {card.icon}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {card.title}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {card.description}
                  </p>
                </div>
              </div>
              <div className="text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                <FiArrowRight size={20} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            รายการล่าสุดของฉัน
          </h2>
        </div>
        <button
          type="button"
          onClick={fetchRecords}
          disabled={loadingRecords}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-cyan-300 dark:hover:border-cyan-700 transition disabled:opacity-60"
        >
          <FiRefreshCw className={loadingRecords ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <TravelExpenseTable
        records={paginatedRecords}
        loading={loadingRecords}
        deletingId={deletingId}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TravelExpense;
